import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, CheckCircle, Mail, Save } from 'lucide-react';

export default function Settings() {
    const { user, updateUserEmail } = useAuth();

    const [email, setEmail] = useState('');
    const [adminNotificationEmail, setAdminNotificationEmail] = useState('');
    const [adminWebhookUrl, setAdminWebhookUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [notificationSuccess, setNotificationSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notificationError, setNotificationError] = useState<string | null>(null);

    useEffect(() => {
        if (user?.email) {
            setEmail(user.email);
        }

        // Fetch existing notification settings if admin
        if (user?.role === 'admin') {
            const fetchSettings = async () => {
                try {
                    const { doc, getDoc } = await import('firebase/firestore');
                    const { db } = await import('../firebase');

                    const emailDoc = await getDoc(doc(db, 'settings', 'email'));
                    if (emailDoc.exists()) {
                        setAdminNotificationEmail(emailDoc.data().admin_notification_email || '');
                    }

                    const notifDoc = await getDoc(doc(db, 'settings', 'notifications'));
                    if (notifDoc.exists()) {
                        setAdminWebhookUrl(notifDoc.data().admin_webhook_url || '');
                    }
                } catch (err) {
                    console.error('Error fetching settings:', err);
                }
            };
            fetchSettings();
        }
    }, [user]);

    const handleUpdateEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        if (!email || email === user?.email) {
            setError('Please enter a new email address.');
            return;
        }

        setLoading(true);
        try {
            await updateUserEmail(email);
            setSuccess(true);
        } catch (err: any) {
            setError(err.message || 'Failed to update email address.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateNotifications = async (e: React.FormEvent) => {
        e.preventDefault();
        setNotificationError(null);
        setNotificationSuccess(false);

        if (user?.role !== 'admin') {
            setNotificationError('Only admins can update platform notifications.');
            return;
        }

        setLoading(true);
        try {
            const { doc, setDoc } = await import('firebase/firestore');
            const { db } = await import('../firebase');

            // Update email settings
            await setDoc(doc(db, 'settings', 'email'), {
                admin_notification_email: adminNotificationEmail
            }, { merge: true });

            // Update webhook settings
            await setDoc(doc(db, 'settings', 'notifications'), {
                admin_webhook_url: adminWebhookUrl
            }, { merge: true });

            setNotificationSuccess(true);
        } catch (err: any) {
            console.error('Error updating notification settings:', err);
            setNotificationError(err.message || 'Failed to update notification settings.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Account Settings</h1>
                    <p className="text-gray-400 mt-1">Manage your account preferences and contact details</p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
                <div className="card">
                    <h2 className="text-lg font-semibold text-white mb-4">Contact Details</h2>

                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400">
                            <AlertCircle size={20} className="mt-0.5 shrink-0" />
                            <div className="text-sm">
                                <p className="font-medium">Update failed</p>
                                <p className="text-red-400/80 mt-1">{error}</p>
                            </div>
                        </div>
                    )}

                    {success && (
                        <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-start gap-3 text-emerald-400">
                            <CheckCircle size={20} className="mt-0.5 shrink-0" />
                            <div className="text-sm">
                                <p className="font-medium">Email updated successfully</p>
                                <p className="text-emerald-400/80 mt-1">Your contact email has been changed to {email}.</p>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleUpdateEmail} className="space-y-4 max-w-md">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-field pl-12"
                                    placeholder="your@email.com"
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || email === user?.email}
                            className="btn-primary flex items-center gap-2"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    <Save size={20} />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {user?.role === 'admin' && (
                    <div className="card">
                        <h2 className="text-lg font-semibold text-white mb-4">Platform Notifications</h2>
                        <p className="text-gray-400 text-sm mb-6">Configure where system alerts and new affiliate applications are sent.</p>

                        {notificationError && (
                            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400">
                                <AlertCircle size={20} className="mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <p className="font-medium">Update failed</p>
                                    <p className="text-red-400/80 mt-1">{notificationError}</p>
                                </div>
                            </div>
                        )}

                        {notificationSuccess && (
                            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-start gap-3 text-emerald-400">
                                <CheckCircle size={20} className="mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <p className="font-medium">Settings updated successfully</p>
                                    <p className="text-emerald-400/80 mt-1">Notification preferences have been saved.</p>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleUpdateNotifications} className="space-y-4 max-w-md">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Admin Notification Email
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                    <input
                                        type="email"
                                        value={adminNotificationEmail}
                                        onChange={(e) => setAdminNotificationEmail(e.target.value)}
                                        className="input-field pl-12"
                                        placeholder="admin@yourdomain.com"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Where new affiliate application alerts will be sent.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Admin Webhook URL (Discord / Slack / Make)
                                </label>
                                <div className="relative">
                                    <input
                                        type="url"
                                        value={adminWebhookUrl}
                                        onChange={(e) => setAdminWebhookUrl(e.target.value)}
                                        className="input-field"
                                        placeholder="https://discord.com/api/webhooks/..."
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Webhook URL for push notifications (e.g. Discord, Make.com).</p>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary flex items-center gap-2"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <Save size={20} />
                                        Save Notification Settings
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
